import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import {
  Fingerprint,
  QrCode2,
  VisibilityOutlined,
  VisibilityOffOutlined,
  PrecisionManufacturingOutlined,
  VpnKeyOutlined,
  AssessmentOutlined,
  ArrowForward,
  DarkModeOutlined,
  LightModeOutlined,
} from "@mui/icons-material";
import { useApp } from "../contexts/AppContext";
import { getReturnPath } from "../utils/navigation";
import { useColorMode } from "../contexts/ColorModeContext";
import { authService, webauthnService } from "../services/api";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";

const LoginPage: React.FC = () => {
  const { mode, toggleColorMode } = useColorMode();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [oauthError, setOauthError] = useState("");
  const [oauthLoading, setOauthLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const { login, loginWithSession, state } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getReturnPath(location.state);

  const handlePasskeyLogin = async () => {
    setOauthError("");
    setPasskeyLoading(true);
    try {
      const data = await webauthnService.loginWithPasskey();
      if (data?.token && data?.user) {
        loginWithSession(data.token, data.user);
        navigate(returnPath, { replace: true });
      } else {
        setOauthError("Accesso con passkey non riuscito");
      }
    } catch (err: any) {
      if (err?.name !== "NotAllowedError" && err?.name !== "AbortError") {
        setOauthError(
          err?.response?.data?.error?.message ||
            "Accesso con passkey non riuscito",
        );
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setOauthError("");
    try {
      await login(email, password);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleOAuthSuccess = async (
    provider: "google" | "apple",
    idToken: string,
    displayName?: string,
  ) => {
    setOauthError("");
    setOauthLoading(true);
    try {
      let response;
      if (provider === "google") {
        response = await authService.googleLogin(idToken);
      } else {
        response = await authService.appleLogin(idToken, displayName);
      }

      const data = response?.data || response;

      if (data?.needsRegistration) {
        // User doesn't have an account — redirect to register
        navigate("/register");
        return;
      }

      if (data?.token) {
        localStorage.setItem("authToken", data.token);
        if (data.refreshToken)
          localStorage.setItem("refreshToken", data.refreshToken);
        if (data.user) {
          const role = (
            data.user.roles?.[0] ||
            data.user.role ||
            ""
          ).toLowerCase();
          const normalizedUser = {
            id: data.user.id,
            name: data.user.displayName || data.user.name || "Utente",
            email: data.user.email,
            companyId: data.user.companyId || "",
            role: role === "customer" ? "client" : role || "viewer",
          };
          localStorage.setItem("user", JSON.stringify(normalizedUser));
        }
        window.location.href = "/dashboard";
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.error?.message || `Errore login ${provider}`;
      setOauthError(msg);
    } finally {
      setOauthLoading(false);
    }
  };

  const displayError = oauthError || state.auth.error;

  const busy = state.auth.loading || oauthLoading || passkeyLoading;
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(340px, .9fr) minmax(450px, 1.1fr)",
        },
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="aside"
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          p: { md: 5, xl: 8 },
          bgcolor: "#101d33",
          color: "#f3f6ff",
          position: "relative",
          overflow: "hidden",
          backgroundImage:
            "linear-gradient(rgba(136,160,205,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(136,160,205,.035) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              bgcolor: "#4266e9",
              width: 40,
              height: 40,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              fontWeight: 800,
            }}
          >
            CM
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={18}>
              Laser Control
            </Typography>
            <Typography
              fontSize={10}
              sx={{ color: "#9caeca", letterSpacing: ".13em" }}
            >
              INDUSTRIAL PLATFORM
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ py: 7, maxWidth: 480 }}>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: ".12em",
              color: "#9eb4ff",
              fontWeight: 700,
              mb: 2,
            }}
          >
            IL TUO SPAZIO OPERATIVO
          </Typography>
          <Typography
            component="p"
            sx={{
              fontSize: { md: 34, xl: 44 },
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: "-.035em",
              mb: 2.5,
            }}
          >
            Il controllo delle tue macchine, in un unico posto.
          </Typography>
          <Typography
            sx={{ fontSize: 14, color: "#a5b5ce", lineHeight: 1.8, mb: 5 }}
          >
            Una console per collegare clienti, software e produzione. Dalla
            verifica di una licenza al lavoro della prossima macchina.
          </Typography>
          <Stack spacing={3}>
            {[
              {
                icon: <PrecisionManufacturingOutlined />,
                title: "Parco macchine",
                detail: "Stato, configurazione e assistenza remota.",
              },
              {
                icon: <VpnKeyOutlined />,
                title: "Licenze e postazioni",
                detail: "Scadenze, coperture e computer autorizzati.",
              },
              {
                icon: <AssessmentOutlined />,
                title: "Dati di produzione",
                detail: "Lavorazioni, tempi e utilizzo delle risorse.",
              },
            ].map((item) => (
              <Stack
                direction="row"
                spacing={2}
                key={item.title}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(129,159,255,.08)",
                    border: "1px solid rgba(129,159,255,.16)",
                    color: "#9eb4ff",
                    borderRadius: 2,
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography fontSize={13} fontWeight={700}>
                    {item.title}
                  </Typography>
                  <Typography fontSize={12} sx={{ color: "#94a6c2", mt: 0.3 }}>
                    {item.detail}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>
        <Typography sx={{ fontSize: 11, color: "#7e91af" }}>
          CM · Soluzioni per la gestione industriale
        </Typography>
      </Box>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, sm: 5 },
          py: { xs: 9, md: 6 },
        }}
      >
        <IconButton
          onClick={toggleColorMode}
          aria-label={
            mode === "dark" ? "Passa al tema chiaro" : "Passa al tema scuro"
          }
          sx={{ position: "absolute", top: 20, right: 20 }}
        >
          {mode === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}
        </IconButton>
        <Box sx={{ width: "100%", maxWidth: 390 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ display: { xs: "flex", md: "none" }, mb: 4 }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: "#4266e9",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              CM
            </Box>
            <Typography fontWeight={700}>Laser Control</Typography>
          </Stack>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 27, md: 30 },
              fontWeight: 700,
              letterSpacing: "-.03em",
              mb: 1,
            }}
          >
            Bentornato.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Accedi al tuo spazio di lavoro.
          </Typography>
          {displayError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {displayError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.2}>
              <TextField
                label="Email"
                type="email"
                name="email"
                autoComplete="username"
                size="medium"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                fullWidth
                disabled={busy}
              />
              <TextField
                label="Password"
                name="password"
                autoComplete="current-password"
                size="medium"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                fullWidth
                disabled={busy}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? "Nascondi password" : "Mostra password"
                        }
                        onClick={() => setShowPassword((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        disabled={busy}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlined fontSize="small" />
                        ) : (
                          <VisibilityOutlined fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={busy}
                endIcon={!busy && <ArrowForward fontSize="small" />}
                sx={{ minHeight: 46 }}
              >
                {state.auth.loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Accedi alla piattaforma"
                )}
              </Button>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={
                passkeyLoading ? (
                  <CircularProgress size={17} />
                ) : (
                  <Fingerprint />
                )
              }
              onClick={handlePasskeyLogin}
              disabled={busy || !webauthnService.isSupported()}
              sx={{ minHeight: 42 }}
            >
              Passkey
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<QrCode2 />}
              onClick={() => navigate("/login/qr", { state: location.state })}
              disabled={busy}
              sx={{ minHeight: 42 }}
            >
              QR da telefono
            </Button>
          </Stack>
          <Divider sx={{ my: 3, color: "text.secondary", fontSize: 11 }}>
            oppure continua con
          </Divider>
          {GOOGLE_CLIENT_ID && (
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 1.5,
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                <GoogleLogin
                  onSuccess={(result) => {
                    if (result.credential)
                      void handleOAuthSuccess("google", result.credential);
                  }}
                  onError={() =>
                    setOauthError("Errore durante il login con Google")
                  }
                  text="signin_with"
                  shape="rectangular"
                  width="280"
                />
              </Box>
            </GoogleOAuthProvider>
          )}
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              const w = window as any;
              if (w.AppleID)
                w.AppleID.auth
                  .signIn()
                  .then((res: any) => {
                    const token = res?.authorization?.id_token;
                    const name = res?.user
                      ? `${res.user.name?.firstName || ""} ${res.user.name?.lastName || ""}`.trim()
                      : undefined;
                    if (token) void handleOAuthSuccess("apple", token, name);
                  })
                  .catch(() =>
                    setOauthError("Errore durante il login con Apple"),
                  );
              else setOauthError("Apple Sign-In non disponibile");
            }}
            disabled={busy}
            sx={{
              minHeight: 42,
              color: "text.primary",
              borderColor: "divider",
            }}
          >
            Accedi con Apple
          </Button>
          <Typography
            variant="body2"
            sx={{ mt: 4, textAlign: "center", color: "text.secondary" }}
          >
            Non hai un account?{" "}
            <Link
              component={RouterLink}
              to="/register"
              underline="hover"
              fontWeight={700}
            >
              Registrati
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
export default LoginPage;
